import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { DrillDownState, NavigationLevel, BreadcrumbItem, NavigationContext, QuickAction } from '@/types/drill-down';

interface DrillDownContextValue {
  state: DrillDownState;
  navigateToLevel: (level: NavigationLevel) => void;
  navigateBack: () => void;
  resetNavigation: () => void;
  updateContext: (context: Partial<NavigationContext>) => void;
  addQuickAction: (action: QuickAction) => void;
  removeQuickAction: (actionId: string) => void;
}

const DrillDownContext = createContext<DrillDownContextValue | undefined>(undefined);

export const useDrillDown = () => {
  const context = useContext(DrillDownContext);
  if (!context) {
    throw new Error('useDrillDown must be used within a DrillDownProvider');
  }
  return context;
};

type DrillDownAction =
  | { type: 'NAVIGATE_TO_LEVEL'; payload: NavigationLevel }
  | { type: 'NAVIGATE_BACK' }
  | { type: 'RESET_NAVIGATION' }
  | { type: 'UPDATE_CONTEXT'; payload: Partial<NavigationContext> }
  | { type: 'ADD_QUICK_ACTION'; payload: QuickAction }
  | { type: 'REMOVE_QUICK_ACTION'; payload: string }
  | { type: 'SET_NAVIGATING'; payload: boolean };

const generateBreadcrumbFromLevel = (level: NavigationLevel): BreadcrumbItem => ({
  id: `${level.level}-${Date.now()}`,
  label: level.title,
  route: `/${level.level}`,
  icon: level.level === 'overview' ? 'home' : 'chart'
});

const drillDownReducer = (state: DrillDownState, action: DrillDownAction): DrillDownState => {
  switch (action.type) {
    case 'NAVIGATE_TO_LEVEL':
      const newBreadcrumb = generateBreadcrumbFromLevel(action.payload);
      return {
        ...state,
        breadcrumbs: [...state.breadcrumbs, newBreadcrumb],
        previousLevel: state.currentLevel,
        currentLevel: action.payload,
        isNavigating: false
      };

    case 'NAVIGATE_BACK':
      if (state.breadcrumbs.length <= 1) {
        return state;
      }
      
      const updatedBreadcrumbs = state.breadcrumbs.slice(0, -1);
      return {
        ...state,
        breadcrumbs: updatedBreadcrumbs,
        currentLevel: state.previousLevel || state.currentLevel,
        previousLevel: undefined,
        isNavigating: false
      };

    case 'RESET_NAVIGATION':
      return {
        ...state,
        breadcrumbs: [state.breadcrumbs[0]], // Keep only the root breadcrumb
        currentLevel: {
          level: 'overview',
          title: 'Dashboard',
          data: null,
          actions: [],
          relatedMetrics: []
        },
        previousLevel: undefined,
        isNavigating: false
      };

    case 'UPDATE_CONTEXT':
      return {
        ...state,
        context: { ...state.context, ...action.payload }
      };

    case 'ADD_QUICK_ACTION':
      const existingActionIndex = state.quickActions.findIndex(a => a.id === action.payload.id);
      if (existingActionIndex >= 0) {
        const updatedActions = [...state.quickActions];
        updatedActions[existingActionIndex] = action.payload;
        return { ...state, quickActions: updatedActions };
      }
      return {
        ...state,
        quickActions: [...state.quickActions, action.payload]
      };

    case 'REMOVE_QUICK_ACTION':
      return {
        ...state,
        quickActions: state.quickActions.filter(a => a.id !== action.payload)
      };

    case 'SET_NAVIGATING':
      return {
        ...state,
        isNavigating: action.payload
      };

    default:
      return state;
  }
};

interface DrillDownProviderProps {
  children: ReactNode;
  initialContext: NavigationContext;
}

export const DrillDownProvider: React.FC<DrillDownProviderProps> = ({ 
  children, 
  initialContext 
}) => {
  const initialState: DrillDownState = {
    breadcrumbs: [{
      id: 'dashboard',
      label: 'Dashboard',
      route: '/dashboard',
      icon: 'home'
    }],
    currentLevel: {
      level: 'overview',
      title: 'Dashboard',
      data: null,
      actions: [],
      relatedMetrics: []
    },
    context: initialContext,
    quickActions: [],
    isNavigating: false
  };

  const [state, dispatch] = useReducer(drillDownReducer, initialState);

  const navigateToLevel = useCallback((level: NavigationLevel) => {
    dispatch({ type: 'SET_NAVIGATING', payload: true });
    setTimeout(() => {
      dispatch({ type: 'NAVIGATE_TO_LEVEL', payload: level });
    }, 100);
  }, []);

  const navigateBack = useCallback(() => {
    dispatch({ type: 'SET_NAVIGATING', payload: true });
    setTimeout(() => {
      dispatch({ type: 'NAVIGATE_BACK' });
    }, 100);
  }, []);

  const resetNavigation = useCallback(() => {
    dispatch({ type: 'RESET_NAVIGATION' });
  }, []);

  const updateContext = useCallback((context: Partial<NavigationContext>) => {
    dispatch({ type: 'UPDATE_CONTEXT', payload: context });
  }, []);

  const addQuickAction = useCallback((action: QuickAction) => {
    dispatch({ type: 'ADD_QUICK_ACTION', payload: action });
  }, []);

  const removeQuickAction = useCallback((actionId: string) => {
    dispatch({ type: 'REMOVE_QUICK_ACTION', payload: actionId });
  }, []);

  const value: DrillDownContextValue = {
    state,
    navigateToLevel,
    navigateBack,
    resetNavigation,
    updateContext,
    addQuickAction,
    removeQuickAction
  };

  return (
    <DrillDownContext.Provider value={value}>
      {children}
    </DrillDownContext.Provider>
  );
};